import { forwardRef, type ComponentPropsWithoutRef } from "react"
import * as PopoverPrimitive from "@radix-ui/react-popover"
import { cn } from "@/shared/utils/general/cn"

/**
 * Popover — matches v1 MUI Popover styling.
 *
 * v1 styles ported:
 *   - Paper: 0 6px padding, 8px radius
 *   - Shadow: 0px 12px 32px #1111110F, 0px 8px 4px -8px #1111110D
 */

const Popover = PopoverPrimitive.Root
const PopoverTrigger = PopoverPrimitive.Trigger
const PopoverAnchor = PopoverPrimitive.Anchor

const PopoverContent = forwardRef<
  HTMLDivElement,
  ComponentPropsWithoutRef<typeof PopoverPrimitive.Content>
>(({ className, align = "center", sideOffset = 4, collisionPadding = 8, ...props }, ref) => (
  <PopoverPrimitive.Portal>
    <PopoverPrimitive.Content
      ref={ref}
      align={align}
      sideOffset={sideOffset}
      collisionPadding={collisionPadding}
      className={cn(
        "z-50 w-72 max-h-[var(--radix-popover-content-available-height)] rounded-xl border border-foreground/5 bg-popover p-4 text-popover-foreground shadow-[0px_12px_32px_0px_#1111110F,0px_8px_4px_-8px_#1111110D] outline-none",
        "data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95",
        "data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95",
        "data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
        className,
      )}
      {...props}
    />
  </PopoverPrimitive.Portal>
))
PopoverContent.displayName = PopoverPrimitive.Content.displayName

export { Popover, PopoverTrigger, PopoverContent, PopoverAnchor }
